import { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, Lock, Play } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import ProjectModal from './ProjectModal';

function getYouTubeId(url) {
  if (!url) return null;
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
  return match ? match[1] : null;
}

function getYouTubeThumbnailUrl(url) {
  const videoId = getYouTubeId(url);
  if (!videoId) return null;

  return `https://i.ytimg.com/vi/${videoId}/mqdefault.jpg`;
}

function getYouTubePreviewEmbedUrl(url) {
  const videoId = getYouTubeId(url);
  if (!videoId) return null;

  return `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&mute=1&controls=0&rel=0&playsinline=1&loop=1&playlist=${videoId}&modestbranding=1&iv_load_policy=3`;
}

const INITIAL_VISIBLE_PROJECTS = 6;

const projects = [
  {
    id: 'artoKu',
    title: 'ArtoKu - Smart Finance Manager',
    subtitle: 'Personal Product - Flutter x Firebase',
    description:
      'Smart finance manager built from scratch with Flutter, Firebase, Gemini AI, and WAHA WhatsApp API. Includes AI Voice Input, receipt scanning, and ArtoBot automation.',
    longDescription:
      'ArtoKu is a smart personal finance manager developed end-to-end using Flutter, Firebase, Gemini AI, and WAHA WhatsApp API. Key features include AI Voice Input, AI Receipt Scanner, visual analytics, multi-wallet support, PDF & CSV reports, Dark Mode, and ArtoBot (24/7 WhatsApp bot) to log transactions and check reports without opening the app.',
    tags: ['Flutter', 'Firebase', 'Gemini AI', 'WhatsApp API'],
    image: '/assets/Projects/Artoku/2.png',
    landingPage: 'https://arto-ku-landingpage.vercel.app/',
    apk: 'https://github.com/RestuAgilYA/artoku_app/releases/download/ArtoKu/ArtoKu.v.1.0.0.apk',
    code: 'https://github.com/RestuAgilYA/artoku_app',
    videoUrl: '',
    screenshots: [
      '/assets/Projects/Artoku/2.png',
      '/assets/Projects/Artoku/3.png',
      '/assets/Projects/Artoku/4.png',
      '/assets/Projects/Artoku/5.png',
      '/assets/Projects/Artoku/6.png',
      '/assets/Projects/Artoku/7.png',
      '/assets/Projects/Artoku/8.png',
    ],
    featured: true,
    category: 'Web & App',
  },
  {
    id: 'artoKuLandingPage',
    title: 'ArtoKu Landing Page',
    subtitle: 'Product Website - Next.js',
    description:
      'A conversion-focused website for ArtoKu that showcases AI Voice Input, automatic receipt scanning, analytics, ArtoBot WhatsApp, and direct download flow.',
    longDescription:
      'A product landing page created to communicate ArtoKu value clearly from first view to install action. The content structure highlights key capabilities such as AI Voice Input, Gemini-powered receipt scanning, financial analytics, multi-wallet support, PDF/CSV export, and ArtoBot workflows on WhatsApp. It also presents screenshot walkthroughs and clear resource links (download, releases, and documentation) to improve user onboarding and conversion.',
    tags: ['Next.js', 'TypeScript', 'Framer Motion', 'Landing Page'],
    image:
      '/assets/Projects/Artoku/landingpage.png',
    demo: 'https://arto-ku-landingpage.vercel.app/',
    screenshots: [],
    featured: true,
    category: 'Web & App',
  },
  {
    id: 'weaningFood',
    title: 'Weaning Food Detection System',
    subtitle: 'Higher Education Research Grant',
    description:
      'Developed with YOLOv5, Flask, HTML & CSS Bootstrap. Contributing to reduce stunting rates in Indonesia by detecting weaning food (MP-ASI) ingredients using AI.',
    longDescription:
      'A comprehensive AI-powered system developed with YOLOv5, Flask, HTML & CSS Bootstrap. This project contributes to reducing stunting rates in Indonesia by detecting weaning food (MP-ASI) ingredients using advanced computer vision. The system can identify various food ingredients and provide nutritional information to help parents make informed dietary choices.',
    tags: ['YOLOv5', 'Flask', 'Python', 'Bootstrap'],
    image:
      'https://restuagilya.github.io/Portfolio-Restu-Agil-Yuli-Arjun/assets/image/project/simanis.png',
    demo: 'https://simanisgizi.com/',
    code: 'https://github.com/RestuAgilYA/SiManis-Sistem-Deteksi-Menu-MPASI',
    videoUrl: 'https://youtu.be/UzADnbPJxhk',
    publication: 'https://ejurnal.seminar-id.com/index.php/bits/article/view/6889',
    screenshots: [],
    featured: true,
    category: 'AI/ML',
  },
  {
    id: 'carsPlateDetection',
    title: 'Cars & License Plate Detection',
    subtitle: 'PT. Global Data Inspirasi',
    description:
      'Built with YOLOv8 to monitor vehicles entering/exiting university gates. Achieved Precision 98.8%, Recall 96.8%, mAP50 98.9% with OCR integration.',
    longDescription:
      'AI-powered vehicle monitoring system built with YOLOv8 for university gate surveillance. Achieved industry-leading metrics: Precision 98.8%, Recall 96.8%, mAP50 98.9%. Features OCR integration for automatic license plate text extraction and logging.',
    tags: ['YOLOv8', 'OCR', 'Python', 'Computer Vision'],
    image:
      'https://restuagilya.github.io/Portfolio-Restu-Agil-Yuli-Arjun/assets/image/project/Cars%20and%20Plate%20Number%20Detection.png',
    code: 'https://github.com/RestuAgilYA/cars-plate-number-detection-with-OCR',
    videoUrl: '', // TODO: Add your YouTube video URL here
    publication: 'https://jsi.politala.ac.id/index.php/JSI/article/view/916',
    screenshots: [],
    featured: true,
    category: 'AI/ML',
  },
  {
    id: 'k3SafetyDetection',
    title: 'K3 Safety Equipment Detection',
    subtitle: 'PT. Global Data Inspirasi',
    description:
      'YOLO-based detection system for workplace safety equipment. Designed for hazardous work environments like mining, construction, and road paving.',
    longDescription:
      'YOLO-based real-time detection system for workplace safety (K3) equipment. Designed for hazardous environments including mining, construction, and road paving operations. Detects helmets, vests, goggles, and other safety gear to ensure compliance.',
    tags: ['YOLO', 'Computer Vision', 'Python', 'Safety'],
    image:
      'https://restuagilya.github.io/Portfolio-Restu-Agil-Yuli-Arjun/assets/image/project/Workplace%20Safety%20Equipment%20Detection%20(K3%20Detection).png',
    demo: 'https://drive.google.com/file/d/10jbQ0jGZAPtwRqN6xfCTXPER9BL0yL1p/view?usp=sharing',
    videoUrl: 'https://youtu.be/r71sJmJs_zY', // TODO: Add your YouTube video URL here
    screenshots: [],
    featured: true,
    category: 'AI/ML',
  },
  {
    id: 'odolDetection',
    title: 'ODOL Trucks Detection',
    subtitle: 'PT. Global Data Inspirasi',
    description:
      'AI model for detecting Over Dimension Overloading (ODOL) trucks. Can be embedded in ATCS traffic surveillance cameras.',
    longDescription:
      'AI model for detecting Over Dimension Overloading (ODOL) trucks on highways. Designed for integration with ATCS (Area Traffic Control System) surveillance cameras for real-time traffic safety monitoring.',
    tags: ['YOLO', 'Deep Learning', 'Python'],
    image:
      'https://restuagilya.github.io/Portfolio-Restu-Agil-Yuli-Arjun/assets/image/project/ODOL%20Trucks.png',
    demo: 'https://drive.google.com/file/d/1BchfQw3Iq1XiFO0PeOeBc9IsLjN3GVOV/view?usp=sharing',
    videoUrl: 'https://youtu.be/-8HcueMbMm0', // TODO: Add your YouTube video URL here
    screenshots: [],
    category: 'AI/ML',
  },
  {
    id: 'ublTesting',
    title: 'System Testing UBL Apps',
    subtitle: 'Black Box Testing',
    description:
      'Manual testing of UBL Apps Desktop Version using Black Box Testing methodology with comprehensive documentation.',
    tags: ['QA Testing', 'Black Box', 'Documentation'],
    image:
      'https://restuagilya.github.io/Portfolio-Restu-Agil-Yuli-Arjun/assets/image/project/Pengujian%20UBL%20Apps.png',
    demo: 'https://ublapps.ubl.ac.id/student/login',
    code: 'https://github.com/RestuAgilYA/_restuagil/blob/ed80fe425a3d4c39244b4ca96da9f34547f76bc5/Laporan%20Pengujian%20Kualitas%20Sistem_Whitebox%20dan%20Blackbox%20Testing_Restu%20Agil%20Yuli%20Arjun_23991080.pdf',
    videoUrl: '',
    screenshots: [],
    category: 'QA',
  },
  {
    id: 'medicalRecordsSystem',
    title: 'Medical Records System',
    subtitle: 'Java & MySQL',
    description:
      'Java GUI application integrated with MySQL implementing CRUD operations for efficient healthcare data management.',
    tags: ['Java', 'MySQL', 'GUI', 'CRUD'],
    image:
      'https://restuagilya.github.io/Portfolio-Restu-Agil-Yuli-Arjun/assets/image/project/Rekam%20Medis%20Java.jpeg',
    demo: 'https://www.linkedin.com/posts/restuagilyuliarjun_javaprogramming-hospitalinformationsystem-activity-7149085191905423360-NE_X',
    code: 'https://github.com/RestuAgilYA/javaRekamMedis',
    videoUrl: '',
    screenshots: [],
    category: 'Web & App',
  },
  {
    id: 'ecommerceWordpress',
    title: 'E-Commerce - WordPress',
    subtitle: 'Content Management System',
    description:
      'Simple e-commerce application built with WordPress CMS for practical online store management.',
    tags: ['WordPress', 'CMS', 'E-Commerce'],
    image:
      'https://restuagilya.github.io/Portfolio-Restu-Agil-Yuli-Arjun/assets/image/project/Djogjapoenya.site.png',
    demo: 'https://djogjapoenya.site/',
    videoUrl: '',
    screenshots: [],
    category: 'Web & App',
  },
  {
    id: 'ecommerceDesktopDesign',
    title: 'E-Commerce UI Design (Desktop)',
    subtitle: 'Figma Prototype',
    description:
      'Modern desktop UI/UX design for an e-commerce platform, created as an interactive Figma prototype.',
    tags: ['Figma', 'UI/UX', 'Desktop', 'Prototype'],
    image:
      'https://restuagilya.github.io/Portfolio-Restu-Agil-Yuli-Arjun/assets/image/project/loginpage.png',
    demo: 'https://www.figma.com/proto/9WWeZUy4MZWLSYtcPHwXDA/Desktop-UMKM?node-id=187-5&starting-point-node-id=28%3A23&scaling=scale-down&mode=design',
    videoUrl: '',
    screenshots: [],
    category: 'Web & App',
  },
  {
    id: 'ecommerceMobileDesign',
    title: 'E-Commerce UI Design (Mobile)',
    subtitle: 'Figma Prototype',
    description:
      'Mobile-responsive UI/UX design for an e-commerce platform, created as an interactive Figma prototype.',
    tags: ['Figma', 'UI/UX', 'Mobile', 'Prototype'],
    image:
      'https://restuagilya.github.io/Portfolio-Restu-Agil-Yuli-Arjun/assets/image/project/loginpage%20mobile.png',
    demo: 'https://www.figma.com/proto/ktrZpVtrJymTBRX2qHdX7k/IMK-KLP-4-fix?type=design&node-id=1-3&scaling=scale-down&page-id=0%3A1&starting-point-node-id=1%3A3&show-proto-sidebar=1&mode=design',
    videoUrl: '',
    screenshots: [],
    category: 'Web & App',
  },
  {
    id: 'telkomsigmaCmdb',
    title: 'CMDB (Configuration Management Database)',
    subtitle: 'Telkomsigma - Internal Project (Role: DBA)',
    description:
      'As DBA, I maintained CMDB reliability for configuration item (CI) and asset data, including performance tuning, access control, and data consistency.',
    longDescription:
      'Internal CMDB platform used to centralize configuration items (CI), asset inventories, and service relationship mapping. My role as Database Administrator covered schema governance, indexing strategy, SQL query optimization, backup and recovery procedures, user privilege control, and data quality validation to support incident and change management processes.',
    tags: ['Internal Project', 'CMDB', 'Database Administration', 'Data Reliability'],
    image:
      '/assets/Projects/Telkomsigma/CMDB.png',
    videoUrl: '',
    screenshots: [],
    isInternal: true,
    category: 'Internal',
  },
  {
    id: 'telkomsigmaWati',
    title: 'WATI (Work Auto Response Tools Interface)',
    subtitle: 'Telkomsigma - Internal Project (Role: DBA)',
    description:
      'Internal auto-response and monitoring interface. Based on the portal flow, WATI emphasizes automate, monitor, and respond operations in one dashboard.',
    longDescription:
      'WATI is an internal work auto-response platform with a dashboard-oriented flow for automation, monitoring alerts, identifying assets, and taking quick response actions. As DBA, I focused on database availability, transactional integrity, query performance, and backup strategy so automated responses and monitoring records stay reliable for operations teams.',
    tags: ['Internal Project', 'Automation', 'Monitoring', 'Database Administration'],
    image:
      '/assets/Projects/Telkomsigma/WATI.png',
    referenceUrl: 'https://wati.jesica.id/auth/login',
    videoUrl: '',
    screenshots: [],
    isInternal: true,
    category: 'Internal',
  },
  {
    id: 'telkomsigmaDms',
    title: 'DMS (Document Management System)',
    subtitle: 'Telkomsigma - Internal Project (Role: DBA)',
    description:
      'Internal DMS with AI-driven document comparison and document Q&A capabilities, supported by secure and high-performance data operations.',
    longDescription:
      'Enterprise Document Management System for storing, indexing, and governing organizational documents. The platform includes AI-assisted document comparison and question-answering workflows on document content. My DBA responsibilities covered metadata schema design, search-related indexing support, SQL tuning, backup and restore readiness, data access control, and audit trail reliability.',
    tags: ['Internal Project', 'DMS', 'AI Comparison', 'Database Administration'],
    image:
      '/assets/Projects/Telkomsigma/DMS.png',
    videoUrl: '',
    screenshots: [],
    isInternal: true,
    category: 'Internal',
  },
];

function ProjectCard({ project, index, onClick, labels }) {
  const previewThumbnailUrl = getYouTubeThumbnailUrl(project.videoUrl);
  const previewEmbedUrl = getYouTubePreviewEmbedUrl(project.videoUrl);
  const isInternal = Boolean(project.isInternal);
  const previewImages = useMemo(() => {
    if (Array.isArray(project.screenshots) && project.screenshots.length > 0) {
      return project.screenshots;
    }

    return project.image ? [project.image] : [];
  }, [project.image, project.screenshots]);
  const hasImageCarousel = !previewThumbnailUrl && previewImages.length > 1;
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [isVideoPreviewActive, setIsVideoPreviewActive] = useState(false);
  const pointerStartXRef = useRef(null);
  const suppressNextClickRef = useRef(false);

  const handleCardClick = () => {
    if (isInternal) return;
    if (suppressNextClickRef.current) {
      suppressNextClickRef.current = false;
      return;
    }

    onClick(project);
  };

  useEffect(() => {
    setActiveImageIndex(0);
    setIsVideoPreviewActive(false);
  }, [project.id]);

  useEffect(() => {
    if (!hasImageCarousel) return undefined;

    previewImages.forEach((source) => {
      const image = new Image();
      image.src = source;
    });

    const autoplay = window.setInterval(() => {
      setActiveImageIndex((prev) => (prev + 1) % previewImages.length);
    }, 3200);

    return () => window.clearInterval(autoplay);
  }, [hasImageCarousel, previewImages]);

  const handlePointerDown = (event) => {
    if (!hasImageCarousel) return;
    pointerStartXRef.current = event.clientX;
  };

  const handlePointerUp = (event) => {
    if (!hasImageCarousel || pointerStartXRef.current === null) return;

    const deltaX = event.clientX - pointerStartXRef.current;
    const swipeThreshold = 35;
    pointerStartXRef.current = null;

    if (deltaX > swipeThreshold) {
      suppressNextClickRef.current = true;
      setActiveImageIndex((prev) => (prev - 1 + previewImages.length) % previewImages.length);
    }

    if (deltaX < -swipeThreshold) {
      suppressNextClickRef.current = true;
      setActiveImageIndex((prev) => (prev + 1) % previewImages.length);
    }
  };

  const handlePointerCancel = () => {
    pointerStartXRef.current = null;
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      whileHover={isInternal ? undefined : { y: -10 }}
      className={`group relative glass rounded-2xl overflow-hidden ${isInternal ? 'cursor-not-allowed' : 'cursor-pointer'}`}
      onClick={handleCardClick}
      aria-disabled={isInternal}
    >
      <div className="relative aspect-video overflow-hidden">
        {isInternal && (
          <div className="absolute top-3 left-3 z-10 flex items-center gap-1.5 rounded-full bg-amber-500/20 px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-amber-200 border border-amber-300/40">
            <Lock size={12} />
            {labels.internalBadge}
          </div>
        )}

        {previewThumbnailUrl ? (
          <div
            className="relative w-full h-full"
            onMouseEnter={() => setIsVideoPreviewActive(true)}
            onMouseLeave={() => setIsVideoPreviewActive(false)}
          >
            {isVideoPreviewActive && previewEmbedUrl ? (
              <iframe
                src={previewEmbedUrl}
                title={`${project.title} demo preview`}
                className="w-full h-full pointer-events-none"
                loading="lazy"
                allow="autoplay; encrypted-media; picture-in-picture"
              />
            ) : (
              <img
                src={previewThumbnailUrl}
                alt={`${project.title} video thumbnail`}
                className={`w-full h-full object-cover transition-transform duration-500 ${isInternal ? '' : 'group-hover:scale-110'}`}
                loading="lazy"
                decoding="async"
              />
            )}

            <div
              className={`pointer-events-none absolute inset-0 bg-black/35 flex items-center justify-center transition-opacity ${isVideoPreviewActive ? 'opacity-0' : 'opacity-100'}`}
            >
              <div className="h-12 w-12 rounded-full bg-black/50 text-white flex items-center justify-center border border-white/35">
                <Play size={20} className="ml-0.5" />
              </div>
            </div>
          </div>
        ) : hasImageCarousel ? (
          <div
            className="relative w-full h-full touch-pan-y select-none cursor-grab active:cursor-grabbing"
            onPointerDown={handlePointerDown}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerCancel}
            onPointerLeave={handlePointerCancel}
          >
            <AnimatePresence initial={false}>
              <motion.img
                key={`${project.id}-${previewImages[activeImageIndex]}`}
                src={previewImages[activeImageIndex]}
                alt={`${project.title} preview ${activeImageIndex + 1}`}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                loading="lazy"
                decoding="async"
                initial={{ opacity: 0.65, scale: 1.02 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.45, ease: 'easeOut' }}
              />
            </AnimatePresence>

            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1.5 rounded-full bg-black/40 px-2.5 py-1">
              {previewImages.map((source, idx) => (
                <button
                  type="button"
                  key={source}
                  onClick={(event) => {
                    event.stopPropagation();
                    setActiveImageIndex(idx);
                  }}
                  className="relative inline-flex h-6 w-6 items-center justify-center rounded-full transition-colors hover:bg-white/10"
                  aria-label={`${labels.viewDetails} ${idx + 1}`}
                >
                  <span
                    className={`h-1.5 rounded-full transition-all ${idx === activeImageIndex ? 'w-4 bg-white' : 'w-1.5 bg-white/55'}`}
                  />
                </button>
              ))}
            </div>
          </div>
        ) : (
          <img
            src={project.image}
            alt={project.title}
            className={`w-full h-full object-cover transition-transform duration-500 ${isInternal ? '' : 'group-hover:scale-110'}`}
            loading="lazy"
            decoding="async"
          />
        )}
        <div
          className={`pointer-events-none absolute inset-0 bg-linear-to-t from-black/80 via-black/20 to-transparent transition-opacity duration-300 flex items-center justify-center ${
            isInternal ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
          }`}
        >
          <div
            className={`flex items-center gap-2 px-4 py-2 rounded-full backdrop-blur-md border text-sm font-medium ${
              isInternal
                ? 'bg-amber-500/15 border-amber-300/40 text-amber-100'
                : 'bg-white/10 border-white/20 text-white'
            }`}
          >
            {isInternal ? <Lock size={16} /> : <Eye size={16} />}
            {isInternal ? labels.privateOnly : labels.viewDetails}
          </div>
        </div>
      </div>

      <div className="p-5">
        <div className="flex flex-wrap gap-2 mb-3">
          {project.tags.slice(0, 3).map((tag) => (
            <span key={tag} className="text-[10px] font-mono px-2 py-0.5 rounded-md bg-accent-500/10 text-accent-400 border border-accent-500/20">
              {tag}
            </span>
          ))}
        </div>
        <h3 className="text-heading font-bold text-lg mb-1 group-hover:text-accent-400 transition-colors">
          {project.title}
        </h3>
        <p className="text-muted text-sm line-clamp-2">{project.description}</p>
        {isInternal && (
          <p className="text-[11px] text-amber-200/90 mt-3">{labels.internalHint}</p>
        )}
      </div>
    </motion.div>
  );
}

export default function Projects() {
  const [selectedProject, setSelectedProject] = useState(null);
  const [activeCategory, setActiveCategory] = useState('All');
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_PROJECTS);
  const { t } = useLanguage();

  const localizedProjects = useMemo(
    () => projects.map((project) => {
      const localizedItem = t.projects.items?.[project.id];

      if (!localizedItem) {
        return project;
      }

      return {
        ...project,
        title: localizedItem.title || project.title,
        subtitle: localizedItem.subtitle || project.subtitle,
        description: localizedItem.description || project.description,
        longDescription:
          localizedItem.longDescription ||
          project.longDescription ||
          localizedItem.description ||
          project.description,
      };
    }),
    [t.projects.items],
  );

  const projectCategories = useMemo(
    () => [
      { titleKey: 'All', count: localizedProjects.length },
      { titleKey: 'AI/ML', count: localizedProjects.filter((p) => p.category === 'AI/ML').length },
      { titleKey: 'Web & App', count: localizedProjects.filter((p) => p.category === 'Web & App').length },
      { titleKey: 'QA', count: localizedProjects.filter((p) => p.category === 'QA').length },
      { titleKey: 'Internal', count: localizedProjects.filter((p) => p.category === 'Internal').length },
    ],
    [localizedProjects],
  );

  const filteredProjects = useMemo(
    () => (activeCategory === 'All'
      ? localizedProjects
      : localizedProjects.filter((p) => p.category === activeCategory)),
    [activeCategory, localizedProjects],
  );

  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE_PROJECTS);
  }, [activeCategory]);

  const visibleProjects = filteredProjects.slice(0, visibleCount);
  const hasMoreProjects = visibleCount < filteredProjects.length;

  const loadMoreProjects = () => {
    setVisibleCount((prev) => Math.min(prev + INITIAL_VISIBLE_PROJECTS, filteredProjects.length));
  };

  return (
    <section id="projects" className="relative py-24 overflow-hidden">
      <div className="absolute top-0 left-0 w-96 h-96 bg-accent-500/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-[radial-gradient(circle_at_center,rgba(99,102,241,0.03)_0%,transparent_70%)]" />

      <div className="section-container relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <p className="text-accent-400 font-mono text-sm mb-2">{t.projects.subtitle}</p>
          <h2 className="text-4xl sm:text-5xl font-bold text-heading mb-4">
            {t.projects.title} <span className="gradient-text">{t.projects.titleHighlight}</span>
          </h2>
          <div className="w-24 h-1 bg-linear-to-r from-accent-500 to-cyan-500 mx-auto rounded-full" />
        </motion.div>

        <div className="mb-10">
          <div className="tabs-scroll-wrap">
            <div className="tabs-scroll">
              <div className="tabs-scroll-inner glass p-1.5 rounded-xl mx-auto">
                {projectCategories.map((category) => (
                  <button
                    key={category.titleKey}
                    onClick={() => setActiveCategory(category.titleKey)}
                    className={`relative px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors duration-300 ${
                      activeCategory === category.titleKey
                        ? 'text-heading'
                        : 'text-muted hover:text-heading'
                    }`}
                  >
                    {activeCategory === category.titleKey && (
                      <motion.div
                        layoutId="activeProjectTab"
                        className="absolute inset-0 bg-accent-500/10 rounded-lg"
                        transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                      />
                    )}
                    <span className="relative z-10">
                      {(t.projects.categories?.[category.titleKey] || category.titleKey)} ({category.count})
                    </span>
                  </button>
                ))}
              </div>
            </div>
            <div className="tabs-scroll-hint" aria-hidden="true">
              <span className="tabs-scroll-hint-thumb" />
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence>
            {visibleProjects.map((project, index) => (
              <ProjectCard
                key={project.id}
                project={project}
                index={index}
                onClick={setSelectedProject}
                labels={t.projects}
              />
            ))}
          </AnimatePresence>
        </div>

        {hasMoreProjects && (
          <div className="mt-10 flex justify-center">
            <button
              onClick={loadMoreProjects}
              className="px-6 py-3 rounded-xl border border-accent-500/40 text-accent-300 hover:bg-accent-500/10 hover:text-accent-200 font-medium transition-colors"
            >
              {t.projects.loadMore} ({visibleProjects.length}/{filteredProjects.length})
            </button>
          </div>
        )}

      </div>

      <AnimatePresence>
        {selectedProject && <ProjectModal project={selectedProject} onClose={() => setSelectedProject(null)} />}
      </AnimatePresence>
    </section>
  );
}
